'use client';

import React, {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useLocalStorage } from 'react-use';

export type PageStatusContextProps = {
  // When the user clicks on checkout button, this value has the meal plan id selected. Otherwise is undefined.
  checkoutLoading: string | undefined;
  setCheckoutLoading: Dispatch<SetStateAction<string | undefined>>;
  pageScrollOnLoad?: boolean;
  setPageScrollOnLoad?: Dispatch<SetStateAction<boolean | undefined>>;
  resetPageScrollOnLoad: () => void;
};

type PageStatusProviderProps = {
  debugLoading?: string;
  children: ReactNode;
};

export const PageStatusContext = createContext<PageStatusContextProps>({
  checkoutLoading: undefined,
  pageScrollOnLoad: false,
  setCheckoutLoading: () => null,
  setPageScrollOnLoad: () => null,
  resetPageScrollOnLoad: () => null,
});

/**
 * provides information about page status (loading, ...)
 */
export function PageStatusProvider(props: PageStatusProviderProps) {
  const { children, debugLoading } = props;

  const [checkoutLoading, setCheckoutLoading] = useState<string | undefined>(debugLoading);
  const [pageScrollOnLoad, setPageScrollOnLoad, remove] = useLocalStorage('pageScrollOnLoad', false);

  const memoizedValue = useMemo(
    () => ({
      checkoutLoading,
      setCheckoutLoading,
      pageScrollOnLoad,
      setPageScrollOnLoad,
      resetPageScrollOnLoad: remove,
    }),
    [checkoutLoading, setCheckoutLoading, pageScrollOnLoad, setPageScrollOnLoad]
  );

  return <PageStatusContext.Provider value={memoizedValue}>{children}</PageStatusContext.Provider>;
}

/**
 * returns the current page status
 */
export function usePageStatus(): PageStatusContextProps {
  const context = useContext(PageStatusContext);

  if (context === undefined) {
    throw new Error('usePageStatus must be within a PageStatusProvider');
  }

  return context;
}
